/** Pure helpers about job board providers, safe to use in the browser. */

export type Provider = 'greenhouse' | 'lever' | 'ashby'

export const PROVIDERS: Array<{ value: Provider, label: string }> = [
    { value: 'greenhouse', label: 'Greenhouse' },
    { value: 'lever', label: 'Lever' },
    { value: 'ashby', label: 'Ashby' }
]

export const isProvider = (value: unknown): value is Provider =>
    value === 'greenhouse' || value === 'lever' || value === 'ashby'

export const providerLabel = (provider: string): string =>
    PROVIDERS.find(item => item.value === provider)?.label ?? provider

/** The public careers board a person can open in a browser. */
export const boardUrl = (provider: string, token: string): string => {
    if (provider === 'lever') return `https://jobs.lever.co/${token}`
    if (provider === 'ashby') return `https://jobs.ashbyhq.com/${token}`
    return `https://job-boards.greenhouse.io/${token}`
}

/** Reads provider and board token out of a careers page link. */
export const detectSource = (input: string): { provider: Provider, token: string } | null => {
    const value = input.trim()
    if (!value) return null

    let url: URL

    try {
        url = new URL(value.startsWith('http') ? value : `https://${value}`)
    }
    catch {
        return null
    }

    const host = url.hostname.toLowerCase()
    const segments = url.pathname.split('/').filter(Boolean)
    const tokenFromQuery = url.searchParams.get('for')

    if (host.endsWith('greenhouse.io')) {
        if (host.startsWith('boards-api.')) {
            const index = segments.indexOf('boards')
            const token = index >= 0 ? segments[index + 1] : undefined
            return token ? { provider: 'greenhouse', token: token.toLowerCase() } : null
        }

        const token = tokenFromQuery ?? (segments[0] === 'embed' ? undefined : segments[0])
        return token ? { provider: 'greenhouse', token: token.toLowerCase() } : null
    }

    if (host.endsWith('lever.co')) {
        const token = host.startsWith('api.') ? segments[2] : segments[0]
        return token ? { provider: 'lever', token: token.toLowerCase() } : null
    }

    if (host.endsWith('ashbyhq.com')) {
        const token = host.startsWith('api.') ? segments[segments.length - 1] : segments[0]
        return token ? { provider: 'ashby', token } : null
    }

    return null
}
